import { adsApi } from '@/api/client';
import { downloadTextFile, reportDateRangeFilenamePart, rowsToCsv } from '@/api/csvExport';
import { buildWorkspaceSearchTermsPageBody } from '@/hooks/useWorkspaceSearchTermsPage';
import {
  WORKSPACE_METRIC_COLUMNS,
  type WorkspaceMetricColumnDef,
} from '@/utils/workspaceAdProductMetrics';
import type { MetricFilterPayload } from '@/utils/workspaceMetricFilters';
import type { WorkspaceEntityColumnDef } from '@/components/workspace/workspaceColumnConfig';
import { formatAutoBiddingSummary, parseAutoBiddingSettings } from '@/utils/autoBidding';

export const WORKSPACE_EXPORT_PAGE_SIZE = 100;
export const WORKSPACE_EXPORT_SOFT_CAP = 10_000;

export type WorkspaceExportColumn = { id: string; label: string };

export type WorkspaceExportResult = {
  rowCount: number;
  truncated: boolean;
};

function parsePageRows(data: unknown): { rows: Record<string, unknown>[]; totalCount: number } {
  if (!data || typeof data !== 'object') return { rows: [], totalCount: 0 };
  const raw = data as Record<string, unknown>;
  const rows = Array.isArray(raw.rows) ? (raw.rows as Record<string, unknown>[]) : [];
  const totalCount = typeof raw.totalCount === 'number' ? raw.totalCount : rows.length;
  return { rows, totalCount };
}

export function workspaceExportColumns(
  visibleIds: string[],
  entityColumns: WorkspaceEntityColumnDef[],
  metricColumns: WorkspaceMetricColumnDef[] = WORKSPACE_METRIC_COLUMNS
): WorkspaceExportColumn[] {
  const labels = new Map<string, string>();
  for (const col of entityColumns) labels.set(col.id, col.label);
  for (const col of metricColumns) labels.set(col.key, col.label);
  return visibleIds
    .filter((id) => labels.has(id))
    .map((id) => ({ id, label: labels.get(id) ?? id }));
}

function metricValue(metrics: unknown, key: string): string | number {
  if (!metrics || typeof metrics !== 'object') return '';
  const value = (metrics as Record<string, unknown>)[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : value;
  }
  return '';
}

export function workspaceRowCellValue(
  columnId: string,
  row: Record<string, unknown>,
  extras?: {
    campaignNameById?: Map<string, string>;
    budgetByCampaignKey?: Map<string, number | null>;
  }
): string | number {
  if (columnId === 'campaign' || columnId === 'campaignName') {
    const campaignId = String(row.campaignId ?? '');
    return extras?.campaignNameById?.get(campaignId) ?? String(row.campaign ?? campaignId ?? '');
  }
  if (columnId === 'budget') {
    const key = String(row.campaignKey ?? '');
    const fromExtra = extras?.budgetByCampaignKey?.get(key);
    if (typeof fromExtra === 'number' && Number.isFinite(fromExtra)) return fromExtra;
    const direct = row.budget;
    return typeof direct === 'number' && Number.isFinite(direct) ? direct : '';
  }
  if (columnId === 'autoBidding') {
    if (row.autoBiddingEnabled !== true) return 'Off';
    return formatAutoBiddingSummary(parseAutoBiddingSettings(row.autoBidding)) || 'On';
  }
  if (columnId === 'outOfBudgetBoost') {
    return row.outOfBudgetBoostEnabled === true ? 'On' : 'Off';
  }
  if (columnId in row && columnId !== 'metrics') {
    const value = row[columnId];
    if (value == null) return '';
    if (typeof value === 'number' || typeof value === 'string') return value;
    return String(value);
  }
  return metricValue(row.metrics, columnId);
}

export function exportWorkspaceTableCsv(args: {
  filenamePrefix: string;
  startDate: string;
  endDate: string;
  columns: WorkspaceExportColumn[];
  rows: Record<string, unknown>[];
  extras?: {
    campaignNameById?: Map<string, string>;
    budgetByCampaignKey?: Map<string, number | null>;
  };
}): WorkspaceExportResult {
  const headers = args.columns.map((c) => c.label);
  const csvRows = args.rows.map((row) =>
    args.columns.map((col) => workspaceRowCellValue(col.id, row, args.extras))
  );
  const csv = rowsToCsv(headers, csvRows);
  const rangePart = reportDateRangeFilenamePart(args.startDate, args.endDate);
  downloadTextFile(`${args.filenamePrefix}_${rangePart}.csv`, csv);
  return { rowCount: args.rows.length, truncated: false };
}

export async function fetchAllWorkspaceEntityRows(args: {
  profileId: string;
  entity: 'portfolios' | 'campaigns' | 'ad-groups' | 'keywords';
  startDate: string;
  endDate: string;
  columnFilters?: {
    search?: string;
    status?: string;
    adProduct?: string;
    matchType?: string;
    campaignStatus?: string;
  };
  appliedFilter?: Record<string, unknown>;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  metricFilters?: MetricFilterPayload[];
  requireActivity?: boolean;
  softCap?: number;
}): Promise<{ rows: Record<string, unknown>[]; truncated: boolean; totalCount: number }> {
  const softCap = args.softCap ?? WORKSPACE_EXPORT_SOFT_CAP;
  const rows: Record<string, unknown>[] = [];
  let offset = 0;
  let totalCount = 0;
  let truncated = false;

  while (rows.length < softCap) {
    const res = await adsApi.getWorkspaceEntityPage(args.profileId, args.entity, {
      limit: WORKSPACE_EXPORT_PAGE_SIZE,
      offset,
      startDate: args.startDate,
      endDate: args.endDate,
      columnFilters: args.columnFilters,
      appliedFilter: args.appliedFilter,
      sortBy: args.sortBy,
      sortDir: args.sortDir,
      metricFilters: args.metricFilters,
      requireActivity: args.requireActivity === false ? false : undefined,
    });
    if (!res.success) {
      throw new Error(`Failed to export ${args.entity}`);
    }
    const page = parsePageRows(res.data);
    totalCount = page.totalCount;
    rows.push(...page.rows);
    if (page.rows.length < WORKSPACE_EXPORT_PAGE_SIZE) break;
    offset += WORKSPACE_EXPORT_PAGE_SIZE;
    if (rows.length >= softCap && rows.length < totalCount) {
      truncated = true;
      break;
    }
  }

  return {
    rows: rows.slice(0, softCap),
    truncated: truncated || (totalCount > softCap && rows.length >= softCap),
    totalCount,
  };
}

export async function fetchAllWorkspaceProductRows(args: {
  profileId: string;
  kind: 'ad-products' | 'advertised-products';
  appliedFilter?: Record<string, unknown>;
  state?: string;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  startDate?: string;
  endDate?: string;
  metricFilters?: MetricFilterPayload[];
  softCap?: number;
}): Promise<{ rows: Record<string, unknown>[]; truncated: boolean; totalCount: number }> {
  const softCap = args.softCap ?? WORKSPACE_EXPORT_SOFT_CAP;
  const rows: Record<string, unknown>[] = [];
  let offset = 0;
  let totalCount = 0;
  let truncated = false;
  const fetchPage =
    args.kind === 'ad-products'
      ? adsApi.getWorkspaceAdProductsPage
      : adsApi.getWorkspaceAdvertisedProductsPage;

  while (rows.length < softCap) {
    const res = await fetchPage(args.profileId, {
      limit: WORKSPACE_EXPORT_PAGE_SIZE,
      offset,
      appliedFilter: args.appliedFilter,
      state: args.state,
      search: args.search,
      sortBy: args.sortBy,
      sortDir: args.sortDir,
      startDate: args.startDate,
      endDate: args.endDate,
      metricFilters: args.metricFilters,
    });
    if (!res.success) {
      throw new Error(`Failed to export ${args.kind}`);
    }
    const page = parsePageRows(res.data);
    totalCount = page.totalCount;
    rows.push(...page.rows);
    if (page.rows.length < WORKSPACE_EXPORT_PAGE_SIZE) break;
    offset += WORKSPACE_EXPORT_PAGE_SIZE;
    if (rows.length >= softCap && rows.length < totalCount) {
      truncated = true;
      break;
    }
  }

  return {
    rows: rows.slice(0, softCap),
    truncated: truncated || (totalCount > softCap && rows.length >= softCap),
    totalCount,
  };
}

export async function fetchAllSearchTermRows(args: {
  profileId: string;
  startDate: string;
  endDate: string;
  appliedFilter?: Record<string, unknown>;
  search?: string;
  matchType?: string;
  metricFilters?: MetricFilterPayload[];
  softCap?: number;
}): Promise<{ rows: Record<string, unknown>[]; truncated: boolean; totalCount: number }> {
  const softCap = args.softCap ?? WORKSPACE_EXPORT_SOFT_CAP;
  const rows: Record<string, unknown>[] = [];
  let offset = 0;
  let totalCount = 0;
  let truncated = false;

  while (rows.length < softCap) {
    const res = await adsApi.getWorkspaceSearchTermsPage(
      args.profileId,
      buildWorkspaceSearchTermsPageBody({
        startDate: args.startDate,
        endDate: args.endDate,
        limit: WORKSPACE_EXPORT_PAGE_SIZE,
        offset,
        columnFilters: {
          search: args.search,
          matchType: args.matchType,
        },
        appliedFilter: args.appliedFilter ?? {},
        metricFilters: args.metricFilters,
      })
    );
    if (!res.success) {
      throw new Error('Failed to export search terms');
    }
    const page = parsePageRows(res.data);
    totalCount = page.totalCount;
    rows.push(...page.rows);
    if (page.rows.length < WORKSPACE_EXPORT_PAGE_SIZE) break;
    offset += WORKSPACE_EXPORT_PAGE_SIZE;
    if (rows.length >= softCap && rows.length < totalCount) {
      truncated = true;
      break;
    }
  }

  return {
    rows: rows.slice(0, softCap),
    truncated: truncated || (totalCount > softCap && rows.length >= softCap),
    totalCount,
  };
}

export function exportMetricRollupCsv(args: {
  filenamePrefix: string;
  startDate: string;
  endDate: string;
  labelHeader: string;
  visibleMetricIds: string[];
  metricColumns: WorkspaceMetricColumnDef[];
  rows: Array<{ label: string; metrics: Record<string, unknown> | null | undefined }>;
}): void {
  const metricCols = args.visibleMetricIds
    .map((id) => args.metricColumns.find((c) => c.key === id))
    .filter((c): c is WorkspaceMetricColumnDef => Boolean(c))
    .map((c) => ({ id: c.key, label: c.label }));
  exportWorkspaceTableCsv({
    filenamePrefix: args.filenamePrefix,
    startDate: args.startDate,
    endDate: args.endDate,
    columns: [{ id: 'label', label: args.labelHeader }, ...metricCols],
    rows: args.rows.map((row) => ({
      label: row.label,
      metrics: (row.metrics ?? {}) as Record<string, unknown>,
    })),
  });
}
